import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useFarmService } from "contexts/ServiceProvider";
import { MutationHook, MutationHookResult } from "hooks/hookTypeHelpers";
import FarmService from "services/FarmService";
import { GET_FARM_INFO_QUERY_KEY } from "./useFarmDetail";
import { GET_FARMS_QUERY_KEY } from "../useFarmsList";

type WithdrawFarmMutationResult = MutationHookResult<
  Awaited<ReturnType<FarmService["withdrawFarm"]>>,
  { farmId: string }
>;

const useWithdrawFarmMutation = (): WithdrawFarmMutationResult => {
  const farmService = useFarmService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["withdraw-farm"],
    mutationFn: ({ farmId }) => farmService.withdrawFarm(farmId),
    onSuccess: async (_, { farmId }) => {
      await queryClient.refetchQueries({
        queryKey: [GET_FARMS_QUERY_KEY],
      });
      queryClient.removeQueries({
        queryKey: [GET_FARM_INFO_QUERY_KEY, { id: farmId }],
      });
    },
  });
};

const useWithdrawFarm = (): MutationHook<
  WithdrawFarmMutationResult,
  "onWithdraw"
> => {
  const {
    mutate: onWithdraw,
    mutateAsync: onWithdrawAsync,
    ...rest
  } = useWithdrawFarmMutation();

  return { onWithdraw, onWithdrawAsync, ...rest };
};

export default useWithdrawFarm;
